import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, setStudentId, goToGoogleLogin } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      setStudentId(data.id);
      toast.success(`Welcome back, ${data.name}!`);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />
      <div className="relative z-10 w-full max-w-md bg-card rounded-3xl shadow-2xl p-6 sm:p-8 border border-border" data-testid="login-page">
        <div className="flex items-center gap-3 mb-2">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_student-genius-26/artifacts/kbshcq5q_Branding-02.png"
            alt="SmartGuru Logo Icon"
            className="w-11 h-11 object-contain"
          />
          <h1 className="text-2xl font-heading font-extrabold bg-gradient-to-r from-[#9A3DB2] via-[#4F4CB9] to-[#E73F9A] bg-clip-text text-transparent">SmartGuru.io</h1>
        </div>
        <p className="text-muted-foreground text-sm mb-6 flex items-center gap-1">
          <LogIn size={14} className="text-secondary" /> Log in to continue learning.
        </p>
        <button
          type="button"
          onClick={() => goToGoogleLogin()}
          data-testid="login-google-button"
          className="w-full h-12 rounded-xl border border-border bg-white text-[#1F1F1F] font-semibold flex items-center justify-center gap-2.5 mb-5 hover:bg-white/90 active:scale-95 transition-all"
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z" /><path fill="#4CAF50" d="M24 44c5.3 0 10.1-1.8 13.9-5l-6.4-5.4C29.5 35.4 26.9 36 24 36c-5.3 0-9.9-3.4-11.5-8.1l-6.6 5.1C9.6 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.5 5.7-6.4 7.3l6.4 5.4C39.4 37.2 44 31.5 44 24c0-1.3-.1-2.7-.4-3.5z" /></svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="login-email">Email</Label>
            <Input id="login-email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@gmail.com" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="login-password">Password</Label>
            <Input id="login-password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="mt-1.5" />
          </div>
          <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full h-12 rounded-xl text-base font-semibold">
            {loading ? "Logging in..." : "Log In"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground text-center mt-6">
          New here?{" "}
          <Link to="/onboarding" data-testid="login-signup-link" className="text-primary font-semibold hover:underline">
            Create a free account
          </Link>
        </p>
      </div>
    </div>
  );
}
